import React from "react";
import { DiscussionWithoutContentDTO } from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { type AnalyticsSurface } from "@/main/analytics/analyticsEvents";
import { onContinueReadingActivated } from "@/main/analytics/discoveryAttribution";
import DiscussionBookmarkToggle from "@/main/react/discussion/DiscussionBookmarkToggle";
import { cn } from "@/lib/utils";
import { isInterruptedRequestError } from "@/main/common/ApiUtils";
import DateFormatter from "@/main/common/DateFormatter";
import { MetaHandler } from "@/main/common/MetaHandler";
import { getDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { WarningBanner } from "@/main/react/component/WarningBanner";
import { Localizer } from "@/main/react/Types";
import { History, X } from "lucide-react";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import {type WithDiscussionAuthorAvatar} from "@/main/common/DiscussionAuthorAvatar";

function getDiscussionHref(discussion: DiscussionWithoutContentDTO): string | undefined {
    const target = discussion.slug?.trim() || discussion.publicId?.trim();
    if (!target) {
        return undefined;
    }
    return `/discussion/${encodeURIComponent(target)}`;
}

export function RecentlyViewedDiscussionCard(props: {
    discussion: DiscussionWithoutContentDTO;
    localizer: Localizer;
    itemPlaywrightId?: string;
    analyticsSurface?: AnalyticsSurface;
    /**
     * `"flush"` drops the card's own surface so it can be a padding-separated row inside a rail panel
     * (SV-135 S6: no inner dividers) — see the home discovery rail. `"card"` (the default) keeps the standalone
     * presentation `HomeFeedList` and `RecentlyViewedDiscussionRail` rely on.
     */
    variant?: "card" | "flush";
    /**
     * The reading-history page's per-row "Remove from history" (QoL `2b`). Absent everywhere else, so the
     * rail and the home feed rows render exactly as before. The parent owns the request and the list;
     * the card only draws the control.
     */
    onRemove?: () => void;
    removing?: boolean;
}) {
    const { discussion, localizer, itemPlaywrightId, analyticsSurface = "home_rail", variant = "card", onRemove, removing = false } = props;
    const flush = variant === "flush";
    const discussionWithAuthorAvatar = discussion as WithDiscussionAuthorAvatar<DiscussionWithoutContentDTO>;
    const dateFormatter = React.useMemo(() => new DateFormatter(localizer), [localizer]);
    const href = getDiscussionHref(discussion);
    const viewedAt = discussion.paginationParameter ? dateFormatter.timeSince(discussion.paginationParameter) : null;
    const viewedLabel = viewedAt
        ? localizer("home.continue_reading.viewed_at", viewedAt)
        : null;
    /*
     * Read off a widened local shape rather than the generated client: `hasNewActivity` is new on the
     * reading-history projection and `src/generated-client` has not been regenerated (that needs a
     * running local backend, and rule 7 forbids hand-editing it). Absent reads as false, which is the
     * pre-existing behaviour — an older server simply shows no cue.
     */
    const hasNewActivity = (discussion as { hasNewActivity?: boolean }).hasNewActivity === true;
    const displayUsername = getDisplayUsername(discussion.authorUsername, localizer);
    const content = (
        <>
            <div className="min-w-0">
                {/* SV-135 S6: in the rail (flush) the title is the row's one loud element. */}
                <div className={flush
                    ? "line-clamp-2 text-[15px] font-bold leading-5 text-foreground"
                    : "line-clamp-2 text-sm font-semibold leading-5 text-text-main"}>
                    {discussion.title}
                </div>
                {hasNewActivity ? (
                    /*
                     * Tier-2 finding 19 — a BOOLEAN cue, never a count.
                     *
                     * `discussion_active_snapshot` refreshes on its own cycle, so a count taken from it
                     * is a number the member can check against the thread and find wrong. "Something
                     * happened here" survives that lag; "three new replies" does not.
                     *
                     * Worded as an observation, not a debt: this says the post moved, it does not say
                     * anyone is waiting on the member (rule 12).
                     */
                    <span
                        data-playwright-id="recently-viewed-new-activity"
                        // SV-135 S6 (A7): the dot plus primary text, with no pill border or fill.
                        className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium leading-none text-primary"
                    >
                        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-primary" />
                        {localizer("home.continue_reading.new_activity")}
                    </span>
                ) : null}
                <AuthorAttribution localizer={localizer}
                    name={displayUsername}
                    imageUrl={discussionWithAuthorAvatar.authorProfileImageUrl}
                    avatarSeed={discussion.authorUsername ?? displayUsername}
                    details={viewedLabel}
                    className="mt-1 text-xs"
                    avatarClassName="size-5"
                />
                {discussion.excerpt?.trim() ? (
                    <p className={flush
                        ? "mt-1 line-clamp-2 text-sm text-muted-foreground"
                        : "mt-2 line-clamp-3 text-xs leading-5 text-neutral-text"}>
                        {discussion.excerpt}
                    </p>
                ) : null}
            </div>
        </>
    );

    // "Save to finish later" is the action most congruent with a resume affordance, and this was the
    // last navigate-only card on either rail. Vote buttons are deliberately absent even though the
    // query already carries the flags: agreeing with a post you have not finished reading is an
    // incoherent ask. See designs/04-live/2026-07-02-actionable-recommendation-rails-design.md.
    //
    // The toggle must sit OUTSIDE the card anchor — an interactive control nested inside an <a> is
    // invalid HTML and swallows the click. In `flush` it moves to the row's top-right, which is where a
    // save affordance belongs anyway and costs no row of its own; in `card` it keeps the footer.
    const bookmarkToggle = discussion.publicId ? (
        <DiscussionBookmarkToggle
            discussionPublicId={discussion.publicId}
            discussionSlug={discussion.slug ?? undefined}
            initiallySaved={Boolean(discussion.isAddedToBookmarksSeries)}
            localizer={localizer}
            playwrightId="continue-reading-bookmark"
        />
    ) : null;

    // Remove sits at the footer's START, away from the bookmark at its end: the two are opposite
    // verbs on one row, and the end band is the one a phone's floating Create button covers.
    const removeControl = onRemove ? (
        <button
            type="button"
            onClick={onRemove}
            disabled={removing}
            aria-label={localizer("reading_history.remove")}
            title={localizer("reading_history.remove")}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60 [@media(pointer:coarse)]:size-11"
            data-playwright-id="reading-history-remove"
        >
            <X className="size-4" aria-hidden="true" />
        </button>
    ) : null;

    const bookmarkFooter = !flush && (bookmarkToggle || removeControl) ? (
        <div className={cn(
            "mt-2.5 flex items-center border-t border-border/50 pt-2.5",
            removeControl ? "justify-between" : "justify-end",
        )}>
            {removeControl}
            {bookmarkToggle}
        </div>
    ) : null;

    const surfaceClassName = flush
        // Flush: no surface of its own. `-mx-2 px-2` lets the hover wash and the divider bleed past the
        // copy without indenting it — the rail panel's padding is the only inset left.
        ? "-mx-2 rounded-lg px-2 py-3 transition-colors first:pt-0 last:pb-0 hover:bg-muted/45"
        : "rounded-2xl border border-border bg-muted p-3";

    const body = href ? (
        <a
            href={href}
            className="block min-w-0 flex-1"
            onClick={() => onContinueReadingActivated(analyticsSurface, discussion.publicId)}
        >
            {content}
        </a>
    ) : (
        <div className="min-w-0 flex-1">{content}</div>
    );

    return (
        <div
            className={cn(surfaceClassName, !flush && href && "transition-colors hover:bg-card")}
            data-playwright-id={itemPlaywrightId}
        >
            {flush ? (
                <div className="flex items-start gap-2">
                    {body}
                    {bookmarkToggle ? <div className="shrink-0">{bookmarkToggle}</div> : null}
                </div>
            ) : (
                body
            )}
            {bookmarkFooter}
        </div>
    );
}

export function RecentlyViewedDiscussionCards(props: {
    items: DiscussionWithoutContentDTO[];
    localizer: Localizer;
    itemPlaywrightId?: string;
    className?: string;
    analyticsSurface?: AnalyticsSurface;
    variant?: "card" | "flush";
    /** The reading-history page's per-row Remove (QoL `2b`); see {@link RecentlyViewedDiscussionCard}. */
    onRemove?: (discussion: DiscussionWithoutContentDTO) => void;
    removingIds?: ReadonlySet<string>;
}) {
    const { items, localizer, itemPlaywrightId, className, analyticsSurface, variant, onRemove, removingIds } = props;

    if (items.length === 0) {
        return null;
    }

    return (
        <div className={className ?? "grid gap-3 sm:grid-cols-2"}>
            {items.map((discussion, index) => (
                <RecentlyViewedDiscussionCard
                    key={discussion.publicId ?? discussion.slug ?? `recent-${index}`}
                    discussion={discussion}
                    localizer={localizer}
                    itemPlaywrightId={itemPlaywrightId}
                    analyticsSurface={analyticsSurface}
                    variant={variant}
                    onRemove={onRemove && discussion.publicId ? () => onRemove(discussion) : undefined}
                    removing={Boolean(discussion.publicId && removingIds?.has(discussion.publicId))}
                />
            ))}
        </div>
    );
}

export function RecentlyViewedDiscussionRail(props: {
    localizer: Localizer;
    title: string;
    subtitle: string;
    pageSize?: number;
    className?: string;
    sectionPlaywrightId?: string;
    itemPlaywrightId?: string;
}) {
    const {
        localizer,
        title,
        subtitle,
        pageSize = 4,
        className,
        sectionPlaywrightId = "continue-reading-section",
        itemPlaywrightId = "continue-reading-card",
    } = props;
    const isLoggedIn = MetaHandler.isUserLoggedIn();
    const paginationApi = React.useMemo(() => ClientFactory.getAjaxPaginationRestControllerApi(), []);
    const [items, setItems] = React.useState<DiscussionWithoutContentDTO[]>([]);
    const [loading, setLoading] = React.useState<boolean>(isLoggedIn);
    const [error, setError] = React.useState<string | null>(null);
    // Resolve the error label to a string so the fetch effect depends on its stable value, not the
    // localizer's identity (which would otherwise refetch the rail on every localizer change).
    const loadFailedLabel = localizer("home.discovery.load_failed");

    React.useEffect(() => {
        if (!isLoggedIn) {
            setItems([]);
            setLoading(false);
            setError(null);
            return;
        }

        let active = true;
        setLoading(true);
        setError(null);

        void paginationApi.recentlyViewedDiscussions(undefined, pageSize)
            .then(({ data }) => {
                if (!active) {
                    return;
                }
                setItems(data ?? []);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                if (isInterruptedRequestError(error)) {
                    return;
                }

                setItems([]);
                setLoading(false);
                setError(loadFailedLabel);
            });

        return () => {
            active = false;
        };
    }, [isLoggedIn, loadFailedLabel, pageSize, paginationApi]);

    if (!isLoggedIn || (!loading && !error && items.length === 0)) {
        return null;
    }

    return (
        <section
            className={`rounded-[1.5rem] border border-border bg-card p-4 ${className ?? ""}`.trim()}
            data-playwright-id={sectionPlaywrightId}
        >
            <div className="mb-4 flex items-start gap-3">
                <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-text-main">
                    <History className="size-4.5" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-text-main">{title}</h2>
                    <p className="text-xs text-neutral-text">{subtitle}</p>
                </div>
            </div>

            {loading ? <div className="text-sm text-neutral-text">{localizer("home.loading")}</div> : null}
            {!loading && error ? <WarningBanner description={error} className="py-3" /> : null}
            {!loading && !error ? (
                <RecentlyViewedDiscussionCards
                    items={items}
                    localizer={localizer}
                    itemPlaywrightId={itemPlaywrightId}
                />
            ) : null}
        </section>
    );
}
